/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useConfig } from "../lib/react-query/useConfig";
import { useAnyUnit } from "../lib/react-query/useAnyUnit";
import usePluginUtils from "./usePluginUtils";

export const useProcessedBookingEntities = (bookings = [], invoice = null) => {
  const { data: config } = useConfig();
  const { isPluginEnabled } = usePluginUtils();
  const { data: anyUnit } = useAnyUnit({
    enabled: isPluginEnabled("any_unit"),
    staleTime: Infinity,
    cacheTime: Infinity,
  });
  const priceIncludesTax = config?.price_includes_tax ?? true;
  const bookingEntities = useMemo(() => {
    if (!bookings?.length) {
      return {
        services: [],
        service: null,
        provider: null,
        location: null,
        category: null,
      };
    }

    const firstBooking = bookings[0];

    const applyTax = (service) => {
      if (priceIncludesTax || !service?.tax?.ratio) return service;
      return {
        ...service,
        price: service.price + service.price * service.tax.ratio,
        deposit_price: service.deposit_price != null
          ? service.deposit_price + service.deposit_price * service.tax.ratio
          : service.deposit_price,
      };
    };

    const bookedProvider = firstBooking?.provider || null;
    const provider = bookedProvider && anyUnit?.hide_other_providers
      ? { ...bookedProvider, hide_other_providers: true }
      : bookedProvider;

    return {
      services: bookings.map((booking) => {
        const service = applyTax(booking.service);
        const line = invoice?.lines?.find(
          (l) => l.bookings?.length && l.bookings[0].service_id === booking.service?.id
        );
        return {
          ...service,
          ...(line != null ? { price: line.amount, deposit_price: null, isLineAmount: true } : {}),
          products: booking.products || [],
        };
      }).filter(Boolean),
      service: applyTax(firstBooking?.service) || null,
      provider,
      location: firstBooking?.location || null,
      category: firstBooking?.category || null,
    };
  }, [bookings, priceIncludesTax, anyUnit, invoice]);

  return bookingEntities;
};
